// =====================================================================
// My Agency V2 — state presentation (ARTBOARD-SPEC §4 CMP-EMPTY,
// CMP-SKELETON, CMP-BANNER). Purely presentational.
// =====================================================================
import React from "react";

// ── CMP-EMPTY — empty state tile ─────────────────────────────────────
// icon: 24px symbol node (SYM-30/10/31/14 per board).
// iconTone: "brand" (#EAF3FF) | "success" (#ECFDF5).
// iconShape: "circle" | "tile" (partners: radius 16 square-ish tile).
// small: the compact variant (board 1d "All agents are licensed").
export const EmptyState = ({
  icon,
  iconTone = "brand",
  iconShape = "circle",
  small = false,
  title,
  body,
  bodyMaxWidth = 360,
  cta,
  link,
  padding,
  style,
  ...rest
}) => {
  const iconSize = small ? 44 : 56;
  return (
    <div
      className="flex flex-col items-center bg-white border border-[#E3E9F1] rounded-xl text-center"
      style={{ padding: padding || (small ? "36px 32px" : "48px 32px"), ...style }}
      {...rest}
    >
      <div
        style={{
          width: iconSize,
          height: iconSize,
          borderRadius: iconShape === "tile" ? 16 : "50%",
          background: iconTone === "success" ? "#ECFDF5" : "#EAF3FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </div>
      <div
        className="font-semibold text-[#0F172A]"
        style={{ fontSize: small ? 15 : 16, marginTop: small ? 13 : 16 }}
      >
        {title}
      </div>
      {body && (
        <div
          className="text-[#64748B]"
          style={{
            fontSize: small ? 13 : 13.5,
            lineHeight: 1.5,
            maxWidth: bodyMaxWidth,
            marginTop: small ? 4 : 6,
          }}
        >
          {body}
        </div>
      )}
      {cta && <div style={{ marginTop: 18 }}>{cta}</div>}
      {link && <div style={{ marginTop: 12 }}>{link}</div>}
    </div>
  );
};

// ── CMP-SKELETON — pulsing blocks (token colors + tpPulse) ───────────
// tone: "strong" #E4EAF1 | "light" #EDF1F6 | "chip" #F0F4F9.
// Compose per-board anatomies (1e/2c) at the call site.
const SKELETON_TONES = {
  strong: "#E4EAF1",
  light: "#EDF1F6",
  chip: "#F0F4F9",
};
export const SkeletonBlock = ({
  width = "100%",
  height = 10,
  radius = 6,
  tone = "strong",
  circle = false,
  className = "",
  style,
  ...rest
}) => (
  <div
    className={`animate-tp-pulse ${className}`}
    style={{
      width: circle ? height : width,
      height,
      borderRadius: circle ? "50%" : radius,
      background: SKELETON_TONES[tone] || SKELETON_TONES.strong,
      flex: "none",
      ...style,
    }}
    {...rest}
  />
);

// ── CMP-BANNER — inline banners ──────────────────────────────────────
// variant "warning" (1c amber, centered icon) | "error" (3a red,
// flex-start icon). icon: SYM-12 16 / SYM-13 per board.
export const Banner = ({ variant = "warning", icon, style, children, ...rest }) => {
  const v =
    variant === "error"
      ? {
          alignItems: "flex-start",
          gap: 9,
          background: "#FEF2F2",
          border: "1px solid #FECACA",
          padding: "11px 13px",
          fontSize: 12.5,
          color: "#B91C1C",
          lineHeight: 1.5,
        }
      : {
          alignItems: "center",
          gap: 10,
          background: "#FFFBEB",
          border: "1px solid #FDE68A",
          padding: "11px 14px",
          fontSize: 13,
          color: "#92400E",
          lineHeight: 1.45,
        };
  return (
    <div style={{ display: "flex", borderRadius: 10, ...v, ...style }} {...rest}>
      {icon}
      <span>{children}</span>
    </div>
  );
};

// Info boxes (2d): blue "What this changes" / grey "What stays with …".
// header = the label text; icon = SYM-27 / SYM-28 node.
export const InfoBox = ({ tone = "blue", icon, header, style, children, ...rest }) => {
  const blue = tone === "blue";
  return (
    <div
      style={{
        background: blue ? "#F5F9FF" : "#F8FAFC",
        border: `1px solid ${blue ? "#D6E7FF" : "#EDF1F6"}`,
        borderRadius: 11,
        padding: "13px 15px",
        ...style,
      }}
      {...rest}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12.5,
          fontWeight: 700,
          color: blue ? "#1668D9" : "#64748B",
        }}
      >
        {icon}
        {header}
      </div>
      {children}
    </div>
  );
};

// Bullet list inside InfoBox: 13px #334155 lh 1.5.
export const InfoBoxList = ({ style, children, ...rest }) => (
  <ul
    style={{
      margin: "8px 0 0",
      paddingLeft: 19,
      display: "flex",
      flexDirection: "column",
      gap: 5,
      fontSize: 13,
      color: "#334155",
      lineHeight: 1.5,
      listStyle: "disc",
      ...style,
    }}
    {...rest}
  >
    {children}
  </ul>
);
